import { useQuery } from "@tanstack/react-query";

import { HubPageTitle } from "@/components/HubPageTitle";
import { Card, CardBody } from "@/components/ui/Card";
import { ApiError, apiFetch } from "@/lib/api";
import { Link, Navigate, useParams } from "react-router-dom";

import { InboundContinueStationLink } from "./InboundContinueStationLink";
import type { InboundContinuePlacement } from "./inboundContinuePath";

import {
  formatInboundArrivalUnitHeading,
  parseInboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";

type ArrivalPackagePlacement =
  | "waiting"
  | "holding"
  | "pending_put_away"
  | "put_away"
  | "received"
  | "cancelled";

type ArrivalPackagePage = {
  package_no: number;
  unit_kind: string;
  short_code: string;
  vendor_name: string;
  po_number: string | null;
  carrier: string | null;
  tracking: string | null;
  arrived_at: string;
  process_state: "open" | "received" | "cancelled";
  placement: ArrivalPackagePlacement;
  locations: string[];
  containers: ArrivalPackageContainer[];
};

type ArrivalPackageContainer = {
  hex: string;
  short_code: string;
  placement: "holding" | "pending_put_away" | "put_away";
  location: string | null;
};

function continuePlacement(placement: ArrivalPackagePlacement): InboundContinuePlacement | null {
  if (placement === "waiting" || placement === "holding" || placement === "pending_put_away") {
    return placement;
  }
  return null;
}

function placementLabel(placement: ArrivalPackagePlacement): string {
  switch (placement) {
    case "holding":
      return "On hold";
    case "pending_put_away":
      return "Pending put away";
    case "put_away":
      return "Put away";
    case "received":
      return "Received";
    case "cancelled":
      return "Cancelled";
    default:
      return "Not received yet";
  }
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function detail(label: string, value: string | null): JSX.Element | null {
  const text = value?.trim() ?? "";
  if (text === "") return null;
  return (
    <div>
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd>{text}</dd>
    </div>
  );
}

export function InboundArrivalPackagePage(): JSX.Element {
  const { scanHex = "" } = useParams();
  const query = useQuery({
    queryKey: ["arrival-package", scanHex],
    queryFn: () =>
      apiFetch<ArrivalPackagePage>(`/api/shipping/inbound/arrival-packages/${encodeURIComponent(scanHex)}`),
    enabled: scanHex.trim() !== "",
  });
  const row = query.data;
  const containers = row?.containers ?? [];
  const pageTitle = row != null
    ? formatInboundArrivalUnitHeading(parseInboundArrivalUnitKind(row.unit_kind), row.package_no)
    : "Arrival";
  const errorMessage = query.error instanceof ApiError
    ? query.error.message
    : query.error instanceof Error
      ? query.error.message
      : null;
  const stationPlacement = row == null ? null : continuePlacement(row.placement);

  if (row != null && row.process_state === "received" && containers.length === 1) {
    return <Navigate to={`/i/${containers[0]!.hex}`} replace />;
  }

  return (
    <div className="space-y-4">
      <HubPageTitle breadcrumbLabel={pageTitle}>{pageTitle}</HubPageTitle>
      {query.isLoading ? <p className="text-sm text-muted-foreground">Loading arrival.</p> : null}
      {errorMessage != null ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
      {row != null ? (
        <Card>
          <CardBody className="space-y-3">
            {stationPlacement != null && containers.length === 0 ? (
              <InboundContinueStationLink placement={stationPlacement} arrivalScan={scanHex} />
            ) : stationPlacement == null ? (
              <p className="text-lg font-medium">{placementLabel(row.placement)}</p>
            ) : null}
            {containers.length > 1 ? (
              <ul className="space-y-2">
                {containers.map((container) => {
                  const containerStation = continuePlacement(container.placement);
                  return (
                    <li key={container.hex} className="flex flex-wrap items-center gap-2">
                      <Link className="font-mono text-sm font-medium text-sky-800 hover:underline" to={`/i/${container.hex}`}>
                        {container.short_code}
                      </Link>
                      {containerStation != null ? (
                        <InboundContinueStationLink
                          placement={containerStation}
                          inventoryBoxHex={container.hex}
                        />
                      ) : container.location != null ? (
                        <span className="text-sm text-muted-foreground">{container.location}</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {row.placement === "put_away" && containers.length === 0 && row.locations.length > 0 ? (
              <div>
                <p className="text-sm text-muted-foreground">Current location</p>
                <ul>
                  {row.locations.map((location) => (
                    <li key={location}>{location}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            <p className="font-mono text-sm">{row.short_code}</p>
            <dl className="grid gap-3 sm:grid-cols-2">
              {detail("Vendor", row.vendor_name)}
              {detail("Purchase order", row.po_number)}
              {detail("Carrier", row.carrier)}
              {detail("Tracking", row.tracking)}
              {detail("Arrived", formatWhen(row.arrived_at))}
            </dl>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
